import { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

interface ProtectedRouteProps {
  children: ReactNode
  /** Required role(s) - redirect to correct portal if wrong role */
  requiredRole?: string | string[]
}

/**
 * Protects routes requiring authentication.
 * Redirects to /login if not authenticated.
 * Optionally enforces role and redirects to correct portal.
 */
export default function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-600">Loading...</div>
      </div>
    )
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (requiredRole) {
    const userRole = user.role ?? user.userType
    const allowed = Array.isArray(requiredRole) ? requiredRole : [requiredRole]
    if (userRole && !allowed.includes(userRole)) {
      const redirectTo =
        userRole === 'broker' ? '/broker' :
        userRole === 'admin' ? '/admin' :
        userRole === 'data_operator' ? '/dataentry' :
        '/customer'
      return <Navigate to={redirectTo} replace />
    }
  }

  return <>{children}</>
}
